"use client";
import React from "react";

import { Box, BoxSx } from "../../../foundations";
import { Text } from "../../../foundations/Typography/Text";
import { DropdownItem } from "./DropdownItem";
import { useDynamicDropdownHeight } from "./hooks/useDynamicDropdownHeight";
import { DropdownOption } from "./types";

export interface DropdownMenuProps {
  showNoResults?: boolean;
  dropdownOptions: DropdownOption[];
  focusedIdx: number | null;
  optionDisplayCount?: number | null;
  noResultsMessage?: string;
  sx?: BoxSx;
}

const DropdownItemList = ({
  dropdownOptions,
  focusedIdx
}: {
  dropdownOptions: DropdownOption[];
  focusedIdx: number | null;
}) => {
  if (!dropdownOptions.length) {
    return null;
  }

  return (
    <>
      {dropdownOptions.map((option, idx) => (
        <DropdownItem
          key={option.value}
          option={option}
          isFocused={focusedIdx === idx}
          idx={idx}
        />
      ))}
    </>
  );
};

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  dropdownOptions,
  showNoResults,
  optionDisplayCount = 5,
  focusedIdx,
  noResultsMessage,
  sx
}) => {
  const dropdownRef = React.useRef<HTMLUListElement>(null);

  // Hook to dynamically adjust the height of the dropdown
  const { handleScroll } = useDynamicDropdownHeight({
    dropdownOptions,
    dropdownRef,
    optionDisplayCount,
    showNoResults
  });

  if (!dropdownOptions.length && !showNoResults) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        position: "absolute",
        zIndex: "dropdown",
        pt: "spacing-2",
        pb: "spacing-2",
        top: "100%",
        left: 0,
        border: "1px solid",
        borderColor: "field-stroke",
        borderRadius: "radius-2",
        backgroundColor: "field-surface",
        boxShadow: "shadow-100",
        transition: "transition-color-medium",
        ...sx
      }}
    >
      {dropdownOptions.length > 0 ? (
        <Box
          ref={dropdownRef}
          as="ul"
          sx={{
            listStyle: "none",
            overflowY: "auto"
          }}
          onScroll={handleScroll}
        >
          <DropdownItemList
            dropdownOptions={dropdownOptions}
            focusedIdx={focusedIdx}
          />
        </Box>
      ) : (
        showNoResults && (
          <Box sx={{ px: "spacing-4", py: "spacing-5", textAlign: "center" }}>
            <Text variant="body-1-regular" sx={{ color: "text-incidental" }}>
              {noResultsMessage}
            </Text>
          </Box>
        )
      )}
    </Box>
  );
};
